// The wallet: stored value that funds sessions and purchases (F-WAL-01, -02, -04, -08; spec/06 §5).
//
// A player has one **wallet** per organisation, created on first top-up, closed-loop — spendable
// only at that organisation's venues (ADR-0010). It holds two balances: `cash` (money the player
// paid for) and `bonus` (promotional value from a top-up offer). Value moves only through the
// double-entry ledger (spec/06 §2); the wallet never holds a balance column of its own.
//
// This file is the wallet's HTTP surface:
//
//   POST …/players/{playerId}/wallet/top-ups     add value at the counter, paid by a tender (F-WAL-02)
//   GET  …/players/{playerId}/wallet/balance      the player's cash, bonus and total here (F-WAL-01)
//   POST …/players/{playerId}/wallet/adjustments  staff credit or debit the cash, with a reason (F-WAL-07)
//   POST …/players/{playerId}/wallet/refunds      refund wallet cash to a counter tender (F-WAL-06)
//   GET  /v1/station/sessions/current/wallet      the station reads its session player's balance (F-KIO-07, F-WAL-01)
//
// The wallet **statement** (F-WAL-08) fills the player's existing wallet tab
// (`GET …/players/{playerId}/wallet`, `routes/players.ts`), gated by `players:read`. Spending from
// the wallet is a service seam the sessions/POS layer calls (`WalletService.spend`/`hold`), not an
// endpoint of its own — value moves through the feature that owns the business action, exactly as it
// does for the ledger. Every DTO name is prefixed `Wallet…` so its generated C# type does not
// collide with a hand-written `Clavian.Station.*` type.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { JournalEntryIdSchema, PlayerIdSchema, WalletTransactionIdSchema } from "../ids/schema.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
import { MoneySchema, NonNegativeMoneySchema } from "../primitives/money.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { OrganizationScopeParamsSchema } from "../primitives/scope.js";
import { PERMISSION_EXTENSION } from "./audit.js";
import { IdempotentCreateHeaderSchema } from "./id-checks.js";
import { PLAYERS_READ, WALLET_ADJUST, WALLET_REFUND, WALLET_TOPUP } from "../permissions/index.js";
// ---------------------------------------------------------------------------------------------
// Shared vocabulary (spec/06 §5)
// ---------------------------------------------------------------------------------------------
/** A wallet's two balances and their total, in the organisation's currency (spec/06 §5). */
export const WalletBalanceSchema = z
    .object({
    playerId: PlayerIdSchema,
    /** Real money the player paid for; what a refund gives back (spec/06 §5). */
    cash: NonNegativeMoneySchema,
    /** Promotional value from a top-up offer; spent before it expires, never refunded (spec/06 §5). */
    bonus: NonNegativeMoneySchema,
    /** `cash + bonus`: what the wallet can spend. */
    total: NonNegativeMoneySchema,
    /**
     * Whether the player lets this wallet pay for play time on its own, once their paid time, passes and any comp run
     * out (spec/05 §4.1, F-WAL-04): on by default; the player changes it at the kiosk. Read-only here. Present on the
     * balance read (`getWalletBalance`); absent elsewhere.
     */
    useForPlay: z.boolean().optional(),
    /**
     * Whether the organisation's wallet is turned on (`wallet.enabled`, off by default; spec/06 §5). Off, a top-up, an
     * adjustment, a refund and a wallet spend are refused (`wallet-disabled`), while the balance and statement still
     * read — so a screen says the wallet is off, and where it is turned on, before anyone tries. Present on the balance
     * read (`getWalletBalance`); absent elsewhere, and from a server that predates it.
     */
    enabled: z.boolean().optional(),
})
    .openapi('WalletBalance', { description: 'A player’s wallet balance at one organisation (spec/06 §5, F-WAL-01).' });
/**
 * What one movement on a wallet was (spec/06 §5). `spend` draws it (a session charge or a sale's
 * `wallet` tender); `refund` pays wallet cash back out to a tender (F-WAL-06); `sale_refund` credits
 * wallet cash when a sale is refunded to the wallet (spec/06 §8.7); `carried_over` credits the money a claim of a profile
 * moved from the café's previous system brought (F-IAM-13, ADR-0048 §9).
 */
export const WalletTransactionKindSchema = z
    .enum(['topup', 'topup_bonus', 'spend', 'adjustment', 'refund', 'sale_refund', 'carried_over'])
    .openapi('WalletTransactionKind', { description: 'What moved value on the wallet (spec/06 §5).' });
/** A tender that pays a counter top-up (spec/06 §5, §9.1). `online` is a follow-on (payments). */
export const WalletTenderSchema = z
    .enum(['cash', 'counter_card', 'counter_upi', 'online'])
    .openapi('WalletTender', { description: 'How a top-up is paid (spec/06 §5). Counter tenders today; `online` is a follow-on.' });
/** One line of a wallet's history — a top-up, a bonus grant or a spend (spec/06 §5, F-WAL-08). */
export const WalletTransactionSchema = z
    .object({
    id: WalletTransactionIdSchema,
    kind: WalletTransactionKindSchema,
    occurredAt: IsoDateTimeSchema,
    /** Signed change to the cash balance (a top-up is positive, a spend negative). */
    cashAmount: MoneySchema,
    /** Signed change to the bonus balance. */
    bonusAmount: MoneySchema,
    /** One line a player and staff can read: "Top-up (cash)", "Play at PC 7". */
    description: z.string().min(1).max(200),
    /** The journal entry this movement posted, for tracing the money to the ledger (spec/06 §2). */
    ledgerEntryId: JournalEntryIdSchema,
})
    .openapi('WalletTransaction', { description: 'One movement on a player’s wallet (spec/06 §5).' });
// ---------------------------------------------------------------------------------------------
// POST …/players/{playerId}/wallet/top-ups — add value at the counter (F-WAL-02)
// ---------------------------------------------------------------------------------------------
const WalletPlayerParamsSchema = OrganizationScopeParamsSchema.extend({ playerId: PlayerIdSchema }).openapi('WalletPlayerParams');
/** A counter top-up: the cash to add, the tender that pays, and an optional top-up offer (F-WAL-02, -03). */
export const WalletTopUpRequestSchema = z
    .object({
    /** The cash to add, within the organisation's per-top-up limits (spec/06 §5). */
    amount: NonNegativeMoneySchema,
    /** The tender the counter took for it (spec/06 §5). */
    tender: WalletTenderSchema,
    /** A configured top-up offer (`wallet.topupOffers`) to apply for a bonus, if any (F-WAL-03). */
    offerId: z.string().min(1).max(64).optional(),
})
    .strict()
    .openapi('WalletTopUpRequest', { description: 'Add value to a player’s wallet at the counter (F-WAL-02).' });
/** What a top-up returns: the new balance and the movements it posted (F-WAL-02, -03). */
export const WalletTopUpResultSchema = z
    .object({
    wallet: WalletBalanceSchema,
    /** The cash top-up movement. */
    topUp: WalletTransactionSchema,
    /** The bonus a top-up offer granted, when one applied; null otherwise (F-WAL-03). */
    bonus: WalletTransactionSchema.nullable(),
})
    .openapi('WalletTopUpResult', { description: 'A completed wallet top-up (F-WAL-02).' });
const walletNotFound = problemResponse('No such organisation or player, or no venue you can see named in X-Clavian-Venue.', 'not-found');
export const topUpWalletRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/players/{playerId}/wallet/top-ups',
    operationId: 'topUpWallet',
    tags: ['wallet'],
    summary: 'Top up a player’s wallet at the counter (F-WAL-02)',
    description: 'Adds cash to the player’s wallet at this organisation, paid by a tender, and applies a top-up offer’s bonus ' +
        'if one is named. Name the venue with `X-Clavian-Venue`. The wallet must be enabled (`wallet.enabled`) and the ' +
        'amount within the organisation’s limits. Money moves through the ledger and writes one audit row. Needs ' +
        '`wallet:topup` and an `Idempotency-Key`: a retry with the same key answers the top-up already made and posts ' +
        'nothing twice. Top-ups are not taxed here by default; tax is charged where the money is spent (spec/06 §10.3).',
    [PERMISSION_EXTENSION]: WALLET_TOPUP,
    request: {
        params: WalletPlayerParamsSchema,
        headers: IdempotentCreateHeaderSchema,
        body: { content: { 'application/json': { schema: WalletTopUpRequestSchema } }, required: true },
    },
    responses: {
        201: jsonResponse(WalletTopUpResultSchema, 'The wallet, with the top-up and any bonus it posted.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: walletNotFound,
        409: problemResponse('The wallet is not enabled, the amount is outside its limits, top-ups are taxed at sale here, or cash is taken by someone holding no open drawer (ADR-0031).', 'cash-needs-shift', 'wallet-disabled', 'wallet-limit', 'wallet-topup-tax-at-sale'),
        422: problemResponse('The same key was already used for a different top-up.', 'idempotency-key-reused', 'unprocessable'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// GET …/players/{playerId}/wallet/balance — read the balance (F-WAL-01)
// ---------------------------------------------------------------------------------------------
export const getWalletBalanceRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/players/{playerId}/wallet/balance',
    operationId: 'getWalletBalance',
    tags: ['wallet'],
    summary: 'Read a player’s wallet balance here (F-WAL-01)',
    description: 'The player’s cash, bonus and total at this organisation, in its currency. A player with no wallet yet reads ' +
        'zero. `useForPlay` says whether the player lets the wallet pay for play time on its own (spec/05 §4.1; on ' +
        'with no wallet yet) — staff read it, the player changes it. `enabled` says whether the organisation’s wallet is ' +
        'turned on (`wallet.enabled`): off, top-ups, adjustments and refunds are refused (`wallet-disabled`). Requires ' +
        '`players:read`, like the player’s wallet statement tab.',
    [PERMISSION_EXTENSION]: PLAYERS_READ,
    request: { params: WalletPlayerParamsSchema },
    responses: {
        200: jsonResponse(WalletBalanceSchema, 'The wallet balance.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such organisation, or no such player among its players.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// POST …/players/{playerId}/wallet/adjustments — staff credit or debit the cash (F-WAL-07)
// ---------------------------------------------------------------------------------------------
/** Which way a staff adjustment moves the wallet's cash: a `credit` adds, a `debit` removes (spec/06 §5). */
export const WalletAdjustDirectionSchema = z
    .enum(['credit', 'debit'])
    .openapi('WalletAdjustDirection', { description: 'A staff wallet adjustment adds (`credit`) or removes (`debit`) cash (spec/06 §5).' });
/** A staff adjustment: the direction, the amount and the reason it records (F-WAL-07, spec/07 §6.4). */
export const WalletAdjustRequestSchema = z
    .object({
    /** `credit` adds cash to the wallet; `debit` removes it. */
    direction: WalletAdjustDirectionSchema,
    /** The amount to move, a positive magnitude in the organisation's currency, within the role's limit. */
    amount: NonNegativeMoneySchema,
    /** Why the adjustment was made: required and audited (spec/06 §5, §11). */
    reason: z.string().min(1).max(200),
})
    .strict()
    .openapi('WalletAdjustRequest', { description: 'Staff credit or debit a player’s wallet cash, with a reason (F-WAL-07).' });
/** What an adjustment returns: the new balance and the movement it posted (F-WAL-07). */
export const WalletAdjustResultSchema = z
    .object({
    wallet: WalletBalanceSchema,
    /** The adjustment movement (`kind` = `adjustment`); its `cashAmount` is signed by the direction. */
    adjustment: WalletTransactionSchema,
})
    .openapi('WalletAdjustResult', { description: 'A completed wallet adjustment (F-WAL-07).' });
export const adjustWalletRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/players/{playerId}/wallet/adjustments',
    operationId: 'adjustWallet',
    tags: ['wallet'],
    summary: 'Credit or debit a player’s wallet cash (F-WAL-07)',
    description: 'Staff move a player’s wallet **cash** up (`credit`) or down (`debit`) with a reason. The wallet must be ' +
        'enabled (`wallet.enabled`). The amount may not exceed the effective limit for the member — the lower of ' +
        '`wallet.adjustMaxAmount` and the role’s own limit (spec/07 §6.4) — and a debit may not take the cash below ' +
        'zero. Name the venue with `X-Clavian-Venue`. Money moves through the ledger and writes one audit row. Needs ' +
        '`wallet:adjust` and an `Idempotency-Key`: a retry with the same key answers the adjustment already made.',
    [PERMISSION_EXTENSION]: WALLET_ADJUST,
    request: {
        params: WalletPlayerParamsSchema,
        headers: IdempotentCreateHeaderSchema,
        body: { content: { 'application/json': { schema: WalletAdjustRequestSchema } }, required: true },
    },
    responses: {
        201: jsonResponse(WalletAdjustResultSchema, 'The wallet, with the adjustment it posted.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: walletNotFound,
        409: problemResponse('The wallet is not enabled, the adjustment is over the allowed limit, or a debit would overdraw the cash.', 'wallet-disabled', 'wallet-adjustment-over-limit', 'wallet-insufficient-cash'),
        422: problemResponse('The same key was already used for a different adjustment.', 'idempotency-key-reused', 'unprocessable'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// POST …/players/{playerId}/wallet/refunds — refund wallet cash to a tender (F-WAL-06)
// ---------------------------------------------------------------------------------------------
/**
 * A tender a wallet refund is paid back to (spec/06 §5, §8.7). The counter tenders today; refunding to
 * the original `online` method rides with the payment provider (F-PAY-07), so it is not offered yet.
 */
export const WalletRefundTenderSchema = z
    .enum(['cash', 'counter_card', 'counter_upi'])
    .openapi('WalletRefundTender', { description: 'How wallet cash is refunded (spec/06 §5). Counter tenders today; `online` rides with the provider (F-PAY-07).' });
/** A wallet cash refund: the amount, the tender it goes back to, and the reason (F-WAL-06, spec/06 §5, §8.7). */
export const WalletRefundRequestSchema = z
    .object({
    /** The cash to refund, within what the wallet holds, in the organisation's currency. */
    amount: NonNegativeMoneySchema,
    /** The tender the refund is paid back to (cash at the desk, or the venue's card/UPI). */
    tender: WalletRefundTenderSchema,
    /** Why the cash was refunded: required and audited (spec/06 §5, §11). */
    reason: z.string().min(1).max(200),
})
    .strict()
    .openapi('WalletRefundRequest', { description: 'Refund a player’s wallet cash to a counter tender (F-WAL-06).' });
/** What a refund returns: the new balance and the movement it posted (F-WAL-06). */
export const WalletRefundResultSchema = z
    .object({
    wallet: WalletBalanceSchema,
    /** The refund movement (`kind` = `refund`); its `cashAmount` is the negative of the amount refunded. */
    refund: WalletTransactionSchema,
})
    .openapi('WalletRefundResult', { description: 'A completed wallet cash refund (F-WAL-06).' });
export const refundWalletRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/players/{playerId}/wallet/refunds',
    operationId: 'refundWallet',
    tags: ['wallet'],
    summary: 'Refund a player’s wallet cash to a tender (F-WAL-06)',
    description: 'Reverses wallet **cash** to a counter tender, up to the cash the wallet holds — a bonus balance is never ' +
        'refunded (spec/06 §5). The wallet must be enabled (`wallet.enabled`) and cash refunds allowed ' +
        '(`wallet.cashRefundsAllowed`). Name the venue with `X-Clavian-Venue`. Money moves through the ledger and ' +
        'writes one audit row. Needs `wallet:refund` and an `Idempotency-Key`: a retry with the same key answers the ' +
        'refund already made. Refunding to the original online method, and overdrawing once the cash has been spent ' +
        '(`wallet:refund_negative`), ride in with the payment provider and the top-up-lot tracking (F-PAY-07, F-WAL-05).',
    [PERMISSION_EXTENSION]: WALLET_REFUND,
    request: {
        params: WalletPlayerParamsSchema,
        headers: IdempotentCreateHeaderSchema,
        body: { content: { 'application/json': { schema: WalletRefundRequestSchema } }, required: true },
    },
    responses: {
        201: jsonResponse(WalletRefundResultSchema, 'The wallet, with the refund it posted.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: walletNotFound,
        409: problemResponse('The wallet is not enabled, cash refunds are off, or the refund is more cash than the wallet holds.', 'wallet-disabled', 'wallet-refunds-off', 'wallet-insufficient-cash'),
        422: problemResponse('The same key was already used for a different refund.', 'idempotency-key-reused', 'unprocessable'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// GET /v1/station/sessions/current/wallet — the kiosk shows the playing player's balance (F-KIO-07)
// ---------------------------------------------------------------------------------------------
//
// The kiosk's session bar, player chip and Time screen show the signed-in player's wallet (spec/12 §4,
// §7.1). The station pulls it device-authenticated and hands it to the kiosk over the WebView2 bridge
// (`wallet/balance`, spec/10 §5.3). It is scoped by construction: the station names no player — the
// server reads the player of **this station's own active session**, in the station's organisation, so a
// station can never read another player's wallet. Display data only: every spend is decided server-side.
/** The balance of the player playing on this station (F-KIO-07, F-WAL-01). */
export const StationSessionWalletSchema = z
    .object({
    /** The player on the station's active session. */
    playerId: PlayerIdSchema,
    /** The wallet's cash: real money the player paid for (spec/06 §5). Zero with no wallet yet. */
    balance: NonNegativeMoneySchema,
    /** The promotional bonus balance, or null when the wallet holds none (spec/06 §5). */
    bonus: NonNegativeMoneySchema.nullable(),
    /**
     * "Use my wallet for play time" (spec/05 §4.1, F-WAL-04): whether the player lets this wallet pay for play time on
     * its own once their paid time, passes and any comp run out — on by default. `null` when the venue does not let the
     * wallet fund play here (`sessions.walletFundsPlay` with `wallet.enabled`), so the kiosk shows no switch. Absent from
     * an older api.
     */
    useForPlay: z.boolean().nullable().optional(),
})
    .openapi('StationSessionWallet', { description: 'The wallet balance of the player on a station’s active session (F-KIO-07).' });
export const getStationSessionWalletRoute = createRoute({
    method: 'get',
    path: '/v1/station/sessions/current/wallet',
    operationId: 'getStationSessionWallet',
    tags: ['wallet', 'sessions', 'stations'],
    summary: 'The station reads its session player’s wallet balance (F-KIO-07)',
    description: 'Station-authenticated: `Authorization: Bearer <device token>` (spec/07 §5). Returns the wallet cash and ' +
        'bonus of the player on **this station’s own active session**, at the station’s organisation, for the kiosk ' +
        'session bar and Time screen. The station names no player, so it can read no other wallet. Answers `404` when ' +
        'the station has no active session, the session has no player, or the organisation has not enabled its ' +
        'wallet (`wallet.enabled`) — the kiosk then shows no balance. With it, `useForPlay`: whether the player lets the ' +
        'wallet pay for play time on its own (`null` where the venue does not fund play from the wallet), the kiosk’s ' +
        '"Use my wallet for play time" switch. Display data: every spend is decided server-side.',
    responses: {
        200: jsonResponse(StationSessionWalletSchema, 'The session player’s wallet balance.'),
        400: commonProblemResponses[400],
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        404: problemResponse('This station has no active session with a player, or the wallet is not enabled here.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const WALLET_ROUTES = [topUpWalletRoute, getWalletBalanceRoute, adjustWalletRoute, refundWalletRoute];
//# sourceMappingURL=wallet.js.map